import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import type {
	InlineCommentTopLevelMarkerRefsLocationType,
	InlineCommentTopLevelMarkerRefsType,
} from '@confluence/inline-comments-queries/entry-points/InlineCommentTopLevelMarkerRefs.ts';

// eslint-disable-next-line import/no-cycle
import { checkElementExpandMultiLevelCommon } from './domUtils.ts';

const getElementOffsetFromTopOfPage = (element: HTMLElement) => {
	let elementOffsetTop = 0;
	let nodeToMeasure = element;

	while (nodeToMeasure && !nodeToMeasure.classList.contains('fabric-editor-popup-scroll-parent')) {
		elementOffsetTop += nodeToMeasure.offsetTop;
		nodeToMeasure = nodeToMeasure.offsetParent as HTMLElement;
	}

	return elementOffsetTop;
};

// Function to determine if comment is within expand, and open respective expand with support for multiple levels
export const checkElementExpandInEditorMultiLevel = (element: HTMLElement): void => {
	const expandSelectors = ['.ak-editor-expand'];

	const getExpandButton = (node: HTMLElement): HTMLElement | null => {
		if (node.classList.contains('ak-editor-expand')) {
			const button = node.querySelector('.ak-editor-expand__icon-container');
			return button as HTMLElement | null;
		}
		return null;
	};

	const isExpanded = (button: HTMLElement) => {
		const expandContainer = button.closest('.ak-editor-expand');
		return expandContainer?.classList.contains('ak-editor-expand__expanded') || false;
	};

	const onExpand = () => {
		selectInlineCommentCompoundExperience.addCommonAttributes({ isInExpand: true });
	};

	checkElementExpandMultiLevelCommon(element, {
		isEditor: true,
		expandSelectors,
		getExpandButton,
		isExpanded,
		onExpand,
	});
};

// Function to determine if comment is within expand, and open respective expand
export const checkElementExpandInEditor = (element: HTMLElement): void => {
	checkElementExpandInEditorMultiLevel(element);
};

export const openInlineCommentInEditor = ({
	targetElement,
	targetElementID,
	shouldScroll = true,
}: {
	targetElement: HTMLElement;
	targetElementID: string;
	shouldScroll?: boolean;
}): void => {
	const emitter = getEditorAnnotationEventEmitter();

	checkElementExpandInEditor(targetElement);

	emitter.emit('setselectedannotation', targetElementID);

	window.getSelection()?.setPosition(targetElement);

	shouldScroll && scrollToElementInEditor({ targetElement, viewingRoomOffset: -100 });
};

export const scrollToElementInEditor = ({
	targetElement,
	viewingRoomOffset = 0,
}: {
	targetElement: HTMLElement;
	viewingRoomOffset: number;
}): void => {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const editorScrollableContainer = document.querySelector(
		'.fabric-editor-popup-scroll-parent',
	) as HTMLElement;

	if (editorScrollableContainer) {
		editorScrollableContainer.scrollTo(
			0,
			getElementOffsetFromTopOfPage(targetElement) + viewingRoomOffset, // add viewing room
		);
	}
};

type MissedEditorEventsParams = {
	data: InlineCommentTopLevelMarkerRefsType;
	previousAnnotations: string[];
};

export const updateCommentHighlightsInEditor = ({
	data,
	previousAnnotations,
}: MissedEditorEventsParams): string[] => {
	const emitter = getEditorAnnotationEventEmitter();
	const topLevelCommentMarkerRefs = data?.comments?.nodes?.map(
		(node) => (node?.location as InlineCommentTopLevelMarkerRefsLocationType).inlineMarkerRef,
	) as string[];

	if (topLevelCommentMarkerRefs && topLevelCommentMarkerRefs.length > 0) {
		const newAnnotationsSet = new Set(topLevelCommentMarkerRefs);

		const removedMarkerRefs: string[] = [];
		previousAnnotations.forEach((item) => {
			if (!newAnnotationsSet.has(item)) {
				removedMarkerRefs.push(item);
			}
		});

		let updatedAnnotations = [...previousAnnotations];

		const addedMarkerRefs: string[] = [];
		newAnnotationsSet.forEach((item) => {
			if (!previousAnnotations.includes(item)) {
				addedMarkerRefs.push(item);
			}
		});

		for (const markerRef of addedMarkerRefs) {
			emitter.emit('create', markerRef);
			updatedAnnotations.push(markerRef);
		}

		for (const markerRef of removedMarkerRefs) {
			emitter.emit('resolve', markerRef);
			updatedAnnotations = updatedAnnotations.filter((annotation) => annotation !== markerRef);
		}

		return updatedAnnotations;
	}
	return [];
};
